import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut } from "lib/adminClient";

const COLORS = [
  ["#005C40", "Green"],
  ["#8B1A4A", "Rose"],
  ["#9B7200", "Gold"],
  ["#B8002B", "Red"],
  ["#003d2b", "Dark Green"],
  ["#2d6ab0", "Blue"],
];

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";
const labelCls = "text-[12px] font-semibold text-muted block mb-1.5";

const blankItem = () => ({
  badge: "",
  title: "",
  kicker: "",
  desc: "",
  tags: [],
  statValue: "",
  statLabel: "",
  ctaLabel: "Explore",
  ctaHref: "/academics",
  color: "#005C40",
  featured: false,
});
const blankStep = () => ({ label: "", color: "#005C40" });

function Field({ label, hint, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <span className="text-[11.5px] text-muted mt-1 block">{hint}</span>}
    </div>
  );
}

/** Editor for one institution card. */
function CardEditor({ item, onChange, onRemove, onFeature }) {
  const set = (field, value) => onChange({ ...item, [field]: value });

  return (
    <div
      className="border rounded-xl p-4"
      style={{ borderColor: item.featured ? item.color : "#e2ddd6", borderWidth: item.featured ? 2 : 1 }}
    >
      <div className="flex items-center justify-between mb-3.5">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="featured-card"
            checked={!!item.featured}
            onChange={onFeature}
            className="cursor-pointer"
          />
          <span className="text-[12.5px] font-semibold text-ink">
            Featured card <span className="text-muted font-normal">(solid colour header)</span>
          </span>
        </label>
        <button
          onClick={onRemove}
          title="Remove card"
          className="bg-[#fdeaea] border-none rounded-md px-2.5 py-1.5 cursor-pointer text-xs text-[#c44]"
        >
          🗑 Remove
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[70px_110px_1.6fr_1fr] gap-3 mb-3">
        <Field label="Icon">
          <input
            value={item.icon || ""}
            onChange={(e) => set("icon", e.target.value)}
            placeholder="🎓"
            className={`${inputCls} text-center`}
          />
        </Field>
        <Field label="Badge">
          <input
            value={item.badge}
            onChange={(e) => set("badge", e.target.value)}
            placeholder="KCGS"
            className={`${inputCls} text-center font-semibold`}
          />
        </Field>
        <Field label="Title">
          <input
            value={item.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="Khulna Collegiate Girls' School"
            className={inputCls}
          />
        </Field>
        <Field label="Kicker">
          <input
            value={item.kicker}
            onChange={(e) => set("kicker", e.target.value)}
            placeholder="Est. 1938"
            className={inputCls}
          />
        </Field>
      </div>

      <div className="mb-3">
        <Field label="Description">
          <textarea
            value={item.desc}
            onChange={(e) => set("desc", e.target.value)}
            rows={2}
            placeholder="Short blurb shown under the header."
            className={`${inputCls} resize-y`}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr] gap-3 mb-3">
        <Field label="Tags" hint="Comma separated — shown as pills.">
          <input
            value={(item.tags || []).join(", ")}
            onChange={(e) =>
              set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))
            }
            placeholder="School Section, College Section"
            className={inputCls}
          />
        </Field>
        <Field label="Colour">
          <select
            value={item.color}
            onChange={(e) => set("color", e.target.value)}
            className={`${inputCls} bg-white`}
          >
            {COLORS.map(([hex, name]) => (
              <option key={hex} value={hex}>
                {name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Card style" hint="Solid fills the card with its colour.">
          <select
            value={item.tone || "light"}
            onChange={(e) => set("tone", e.target.value)}
            className={`${inputCls} bg-white`}
          >
            <option value="light">Light</option>
            <option value="solid">Solid</option>
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Stat number">
          <input
            value={item.statValue}
            onChange={(e) => set("statValue", e.target.value)}
            placeholder="2,640"
            className={inputCls}
          />
        </Field>
        <Field label="Stat label">
          <input
            value={item.statLabel}
            onChange={(e) => set("statLabel", e.target.value)}
            placeholder="Students enrolled"
            className={inputCls}
          />
        </Field>
        <Field label="Button text">
          <input
            value={item.ctaLabel}
            onChange={(e) => set("ctaLabel", e.target.value)}
            placeholder="Explore"
            className={inputCls}
          />
        </Field>
        <Field label="Button link" hint="/academics or https://…  — blank hides it.">
          <input
            value={item.ctaHref}
            onChange={(e) => set("ctaHref", e.target.value)}
            placeholder="/academics"
            className={inputCls}
          />
        </Field>
      </div>
    </div>
  );
}

export default function SectionsPage() {
  const [label, setLabel] = useState("");
  const [heading, setHeading] = useState("");
  const [sub, setSub] = useState("");
  const [pathTitle, setPathTitle] = useState("");
  const [steps, setSteps] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = (d) => {
    setLabel(d.label || "");
    setHeading(d.heading || "");
    setSub(d.sub || "");
    setPathTitle(d.pathway?.title || "");
    setSteps(d.pathway?.items || []);
    setItems(d.items || []);
  };

  const [loadFailed, setLoadFailed] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      apply((await adminGet("/admin/content/sections")) || {});
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      const saved = await adminPut("/admin/content/sections", {
        label: label.trim(),
        heading: heading.trim(),
        sub: sub.trim(),
        pathway: {
          title: pathTitle.trim(),
          items: steps.filter((s) => (s.label || "").trim()),
        },
        // Drop blank cards so an empty row can't render as a nameless card.
        items: items.filter((it) => (it.title || "").trim() || (it.badge || "").trim()),
      });
      if (saved) apply(saved);
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}

      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      <AdminCard className="mb-[18px]">
        <AdminCardHead title="Heading" />
        <div className="p-[22px] flex flex-col gap-3.5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            <Field label="Eyebrow label">
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="At a Glance"
                className={inputCls}
              />
            </Field>
            <Field label="Heading" hint="Press Enter for a line break.">
              <textarea
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
                rows={2}
                placeholder={"One campus,\nthree institutions."}
                className={`${inputCls} resize-y font-serif`}
              />
            </Field>
          </div>
          <Field label="Intro paragraph">
            <textarea
              value={sub}
              onChange={(e) => setSub(e.target.value)}
              rows={3}
              className={`${inputCls} resize-y`}
            />
          </Field>
        </div>
      </AdminCard>

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Continuous Pathway"
          action={
            <button
              onClick={() => setSteps([...steps, blankStep()])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Step
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          <Field label="Card title">
            <input
              value={pathTitle}
              onChange={(e) => setPathTitle(e.target.value)}
              placeholder="Continuous Pathway"
              className={inputCls}
            />
          </Field>
          {steps.length === 0 && (
            <div className="text-muted text-sm">No steps — the pathway card is hidden.</div>
          )}
          {steps.map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_200px_auto] gap-2">
              <input
                value={s.label}
                onChange={(e) =>
                  setSteps(steps.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)))
                }
                placeholder="Nursery – Class V"
                className={inputCls}
              />
              <select
                value={s.color}
                onChange={(e) =>
                  setSteps(steps.map((x, idx) => (idx === i ? { ...x, color: e.target.value } : x)))
                }
                className={`${inputCls} bg-white`}
              >
                {COLORS.map(([hex, name]) => (
                  <option key={hex} value={hex}>
                    {name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setSteps(steps.filter((_, idx) => idx !== i))}
                title="Remove step"
                className="bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]"
              >
                🗑
              </button>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Institution Cards"
          action={
            <button
              onClick={() => setItems([...items, blankItem()])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Card
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-4">
          {items.length === 0 && <div className="text-muted text-sm">No cards yet.</div>}
          {items.map((it, i) => (
            <CardEditor
              key={i}
              item={it}
              onChange={(next) => setItems(items.map((x, idx) => (idx === i ? next : x)))}
              onRemove={() => setItems(items.filter((_, idx) => idx !== i))}
              // Only one card can be featured — selecting one clears the rest.
              onFeature={() => setItems(items.map((x, idx) => ({ ...x, featured: idx === i })))}
            />
          ))}
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[13px] text-[#1a9f5e] font-semibold">{status}</span>}
      </div>
    </div>
  );
}
